/**
 * Home Page Component
 *
 * This file implements the main landing page of the application with internationalization support.
 * It demonstrates the use of i18next for multi-language content, React Router's data API for
 * server-side rendering, and responsive design with Tailwind CSS.
 *
 * Key features:
 * - Server-side translation with i18next
 * - Client-side translation with useTranslation hook
 * - SEO-friendly metadata using React Router's meta export
 * - Responsive typography with Tailwind CSS
 */
import type { Route } from "./+types/home";

import { Suspense, lazy } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, data } from "react-router";

import { AuroraText } from "~/core/components/ui/aurora-text";
import { Highlighter } from "~/core/components/ui/highlighter";
import { getCanonicalSiteUrl } from "~/core/lib/site-url.server";
import { ScheduleTable as ProgramScheduleTable } from "~/features/programs/components/schedule-table";
import { DEFAULT_SCHEDULE } from "~/features/programs/lib/schedule-data";

const HeroCarousel = lazy(
  () => import("~/features/home/components/hero-carousel"),
);

const HOME_SEO_TITLE = "윤찬욱 레슬링 (YCU Wrestling)";
const HOME_SEO_DESCRIPTION =
  "윤찬욱 관장이 운영하는 평택 레슬링 체육관, 윤찬욱레슬링(YCU Wrestling)입니다. YCU, ycuwrestling, 레슬링 기초부터 실전 훈련까지 체계적으로 지도합니다.";

/**
 * Cache the homepage HTML at the CDN edge so cold starts of the SSR function
 * don't show up in the user-visible page load. The home page renders only
 * public marketing content (no per-user UI), so it is safe to cache for
 * anonymous visitors. We still vary by Cookie and Accept-Language so visitors
 * with a different locale preference (or active session) don't receive the
 * wrong rendered HTML.
 *
 * - max-age=0          → browsers always revalidate (we keep HTML fresh on the user side)
 * - s-maxage=300       → CDNs (Vercel + Cloudflare) hold a fresh copy for 5 minutes
 * - stale-while-revalidate=86400 → up to 24h, the CDN serves stale HTML instantly
 *                                  while it refreshes in the background, so users
 *                                  never wait for a cold SSR start.
 */
const HOME_CACHE_CONTROL =
  "public, max-age=0, s-maxage=300, stale-while-revalidate=86400";
const HOME_VARY = "Cookie, Accept-Language";

/**
 * Headers function for the homepage
 *
 * Forwards the cache directives produced by the loader (or the defaults above)
 * so they are emitted on the SSR response. The headers export is what React
 * Router actually uses for response headers; without it, loader-level headers
 * are not applied to the document response.
 */
export const headers: Route.HeadersFunction = ({ loaderHeaders }) => ({
  "Cache-Control": loaderHeaders.get("Cache-Control") ?? HOME_CACHE_CONTROL,
  Vary: loaderHeaders.get("Vary") ?? HOME_VARY,
});

/**
 * Meta function for setting page metadata
 *
 * This function keeps the public search snippet language-independent so crawlers
 * do not index the English fallback brand name as the primary site name.
 *
 * @param data - Data returned from the loader function containing SEO metadata
 * @returns Array of metadata objects for the page
 */
export const meta: Route.MetaFunction = ({ data }) => {
  const title = data?.title ?? HOME_SEO_TITLE;
  const description = data?.description ?? HOME_SEO_DESCRIPTION;
  const url = data?.url ?? "";
  const image = url ? `${url}/images/hero/IMG_1108.webp` : undefined;

  return [
    { title },
    { name: "description", content: description },
    { property: "og:type", content: "website" },
    { property: "og:site_name", content: HOME_SEO_TITLE },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    ...(url ? [{ property: "og:url", content: url }] : []),
    ...(image ? [{ property: "og:image", content: image }] : []),
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    ...(image ? [{ name: "twitter:image", content: image }] : []),
  ];
};

/**
 * Loader function for server-side data fetching
 *
 * It returns language-independent SEO metadata for search engines and social
 * sharing. Visible page copy remains localized in the component.
 *
 * @param request - The incoming request, used to derive the public origin.
 * @returns Object with SEO title, description, and canonical home URL.
 */
export async function loader({ request }: Route.LoaderArgs) {
  const url = new URL(request.url);
  const siteUrl = getCanonicalSiteUrl(url.origin);

  return data(
    {
      title: HOME_SEO_TITLE,
      description: HOME_SEO_DESCRIPTION,
      url: siteUrl,
    },
    {
      headers: {
        "Cache-Control": HOME_CACHE_CONTROL,
        Vary: HOME_VARY,
      },
    },
  );
}

/**
 * Home page component
 *
 * This is the main landing page component of the application. It displays a simple,
 * centered layout with a headline and subtitle, both internationalized using i18next.
 *
 * Features:
 * - Uses the useTranslation hook for client-side translation
 * - Implements responsive design with Tailwind CSS
 * - Maintains consistent translations between server and client
 *
 * The component is intentionally simple to serve as a starting point for customization.
 * It demonstrates the core patterns used throughout the application:
 * - Internationalization
 * - Responsive design
 * - Clean, semantic HTML structure
 *
 * @returns JSX element representing the home page
 */
export default function Home({ loaderData }: Route.ComponentProps) {
  const { t } = useTranslation();
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SportsActivityLocation",
    name: HOME_SEO_TITLE,
    alternateName: [
      "윤찬욱레슬링",
      "YCU Wrestling",
      "ycuwrestling",
      "YCU",
      "윤찬욱",
    ],
    description: HOME_SEO_DESCRIPTION,
    url: loaderData.url,
    image: `${loaderData.url}/images/hero/IMG_1108.webp`,
    address: {
      "@type": "PostalAddress",
      streetAddress: "경기대로 1409 4층",
      addressLocality: "평택시",
      addressRegion: "경기도",
      addressCountry: "KR",
    },
    founder: {
      "@type": "Person",
      name: "윤찬욱",
    },
    sport: "Wrestling",
    sameAs: ["https://www.instagram.com/ycuwrestling"],
  };
  const googleMapEmbedUrl =
    "https://www.google.com/maps?q=%EA%B2%BD%EA%B8%B0%20%ED%8F%89%ED%83%9D%EC%8B%9C%20%EA%B2%BD%EA%B8%B0%EB%8C%80%EB%A1%9C%201409%204%EC%B8%B5&output=embed";
  const googleMapExternalUrl =
    "https://www.google.com/maps/search/?api=1&query=%EA%B2%BD%EA%B8%B0%20%ED%8F%89%ED%83%9D%EC%8B%9C%20%EA%B2%BD%EA%B8%B0%EB%8C%80%EB%A1%9C%201409%204%EC%B8%B5";

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <section className="mx-auto flex w-full max-w-6xl flex-col items-center gap-5 px-4 py-5 md:gap-6 md:py-8">
        <h1 className="text-center text-4xl leading-tight font-extrabold tracking-tight md:text-5xl lg:text-6xl">
          <Trans
            i18nKey="home.heroTitle"
            components={{
              aurora: <AuroraText>highlight</AuroraText>,
            }}
          />
        </h1>
        <h2 className="text-muted-foreground max-w-3xl text-center text-base whitespace-pre-line md:text-lg">
          <Trans
            i18nKey="home.heroDescription"
            components={{
              highlight: (
                <Highlighter
                  action="underline"
                  color="#f59e0b"
                  strokeWidth={2}
                  iterations={1}
                >
                  highlight
                </Highlighter>
              ),
            }}
          />
        </h2>

        <div className="flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/etc/contact"
            className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex h-11 items-center justify-center rounded-md px-6 text-sm font-semibold transition-colors"
          >
            {t("home.ctaPrimary")}
          </Link>
          <Link
            to="/programs/schedule"
            className="border-border hover:bg-accent inline-flex h-11 items-center justify-center rounded-md border px-6 text-sm font-semibold transition-colors"
          >
            {t("home.ctaSecondary")}
          </Link>
        </div>

        <div className="mt-1 w-full md:mt-2">
          <Suspense
            fallback={
              <div className="bg-muted/60 border-border relative mx-auto aspect-[16/9] max-h-[52vh] w-full max-w-5xl overflow-hidden rounded-2xl border">
                <img
                  src="/images/hero/IMG_1108.webp"
                  alt="Wrestling gym training photo"
                  width={1920}
                  height={1080}
                  fetchPriority="high"
                  decoding="async"
                  loading="eager"
                  sizes="(max-width: 1024px) 100vw, 1024px"
                  className="h-full w-full object-contain object-top"
                />
              </div>
            }
          >
            <HeroCarousel />
          </Suspense>
        </div>
      </section>

      <section className="mx-auto w-full max-w-5xl px-4 py-10 md:py-14">
        <div className="mb-6 text-center">
          <span className="text-primary text-xs font-semibold tracking-widest uppercase">
            {t("navigation.schedule")}
          </span>
          <h2 className="mt-2 text-2xl font-bold tracking-tight md:text-3xl">
            {t("programsPage.scheduleTitle")}
          </h2>
          <p className="text-muted-foreground mt-2 text-sm md:text-base">
            {t("programsPage.scheduleSubtitle")}
          </p>
        </div>
        <ProgramScheduleTable schedule={DEFAULT_SCHEDULE} />
      </section>

      <section className="mx-auto w-full max-w-5xl px-4 pb-12 md:pb-16">
        <div className="mb-6 text-center">
          <span className="text-primary text-xs font-semibold tracking-widest uppercase">
            {t("home.directionsEyebrow")}
          </span>
          <h2 className="mt-2 text-2xl font-bold tracking-tight md:text-3xl">
            {t("home.directionsTitle")}
          </h2>
          <p className="text-muted-foreground mt-2 text-sm md:text-base">
            {t("home.directionsDescription")}
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-[1.1fr_1fr] md:gap-6">
          <div className="bg-muted/60 border-border relative aspect-[4/3] w-full overflow-hidden rounded-xl border">
            <iframe
              title={t("home.directionsAddress")}
              src={googleMapEmbedUrl}
              className="h-full w-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

          <div className="bg-muted/30 border-border flex flex-col justify-between gap-4 rounded-xl border p-5">
            <div>
              <p className="text-muted-foreground text-sm">
                {t("home.directionsAddressLabel")}
              </p>
              <p className="mt-1 text-base font-medium">
                {t("home.directionsAddress")}
              </p>
            </div>

            <a
              href={googleMapExternalUrl}
              target="_blank"
              rel="noreferrer"
              className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex h-10 items-center justify-center rounded-md px-4 text-sm font-semibold transition-colors"
            >
              {t("home.directionsMapButton")}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
